'use client';

import { type FC, useEffect, useRef, useState } from 'react';
import type { Team } from '@cs-shot/battle-sim';
import { BottomSheet, Button } from '../../ui';
import { Credits } from '../../Credits';
import { cn } from '../../../utils/cn';
import { TICKETS, DEFAULT_TICKET } from './economy';
import { TwoSideSelector } from './TwoSideSelector';
import { WinRangeCard } from './WinRangeCard';
import { useRushMidParty } from './RushMidPartyContext';
import { useRushMidSkins } from '../../../queries/useRushMidQueries';
import { useRushMidConfig } from '../../../queries/useRushMidConfig';
import { useBalance } from '../../../queries/useProfileQueries';
import { useNavigation } from '../../../contexts/NavigationContext';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';

const MODES: { size: number; key: 'mode1' | 'mode2' | 'mode5' }[] = [
  { size: 1, key: 'mode1' },
  { size: 2, key: 'mode2' },
  { size: 5, key: 'mode5' },
];

export interface CreateGameSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (mode: number, ticket: number, side: Team, matchMode: 'ranked' | 'bots') => void;
  initialTicket?: number;
}

export const CreateGameSheet: FC<CreateGameSheetProps> = ({ isOpen, onClose, onCreate, initialTicket }) => {
  const { t } = useLocalTranslation(translations);
  const party = useRushMidParty();
  const navigation = useNavigation();
  const balance = useBalance();
  const { data: savedSkins } = useRushMidSkins();
  const { activeTickets } = useRushMidConfig();
  const visibleTickets = activeTickets.length > 0 ? TICKETS.filter((value) => activeTickets.includes(value)) : TICKETS;
  const [ticket, setTicket] = useState(DEFAULT_TICKET);
  const [mode, setMode] = useState(1);
  const [side, setSide] = useState<Team>('t');
  const [bots, setBots] = useState(false);

  const wasOpen = useRef(false);
  useEffect(() => {
    const opening = isOpen && !wasOpen.current;
    wasOpen.current = isOpen;
    if (!opening) return;
    if (party) {
      setTicket(party.ticket);
      setMode(party.mode);
      setSide(party.side !== 'any' ? party.side : 't');
      setBots(party.matchMode === 'bots');
      return;
    }
    if (initialTicket && visibleTickets.includes(initialTicket)) {
      setTicket(initialTicket);
      setMode(savedSkins?.mode === 1 || savedSkins?.mode === 2 || savedSkins?.mode === 5 ? savedSkins.mode : 1);
      setSide(savedSkins?.side === 't' || savedSkins?.side === 'ct' ? savedSkins.side : 't');
      setBots(false);
      return;
    }
    setTicket(savedSkins?.ticket && visibleTickets.includes(savedSkins.ticket) ? savedSkins.ticket : visibleTickets[0] ?? DEFAULT_TICKET);
    setMode(savedSkins?.mode === 1 || savedSkins?.mode === 2 || savedSkins?.mode === 5 ? savedSkins.mode : 1);
    setSide(savedSkins?.side === 't' || savedSkins?.side === 'ct' ? savedSkins.side : 't');
    setBots(savedSkins?.matchMode === 'bots');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isModeDisabled = (size: number) => !!party && (!party.allowedModes.includes(size) || size < party.members.length);

  useEffect(() => {
    if (!party || !isModeDisabled(mode)) return;
    const valid = MODES.map((m) => m.size).filter((size) => !isModeDisabled(size));
    if (valid.length > 0) setMode(Math.min(...valid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party, mode]);

  return (
      <BottomSheet isOpen={isOpen} onClose={onClose} title={t('createSheetTitle')} subtitle={t('createSheetSubtitle')} heightClass="h-auto max-h-[90vh]">
        <div className="flex flex-col px-4 pb-6">

          <div className="text-sm font-medium text-label-secondary">{t('ticket')}</div>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {visibleTickets.map((value) => (
              <button
                key={value}
                onClick={() => { setTicket(value); setBots(false); }}
                className={cn(
                  'flex items-center justify-center rounded-lg border py-2.5 text-xs font-bold transition-colors',
                  !bots && ticket === value ? 'border-side-t bg-side-t/15 text-label-primary' : 'border-white/10 bg-bg-secondary text-label-secondary',
                )}
              >
                <Credits amount={value} iconSize={12} />
              </button>
            ))}
            <button
              onClick={() => setBots(true)}
              className={cn(
                'flex items-center justify-center rounded-lg border py-2.5 text-xs font-bold transition-colors',
                bots ? 'border-accent-green bg-accent-green/15 text-label-primary' : 'border-white/10 bg-bg-secondary text-label-secondary',
              )}
            >
              {t('practiceBots')}
            </button>
          </div>

          {!bots && <WinRangeCard ticket={ticket} />}


          <div className="mt-4 text-sm font-medium text-label-secondary">{t('format')}</div>
          <div className="mt-2 flex gap-2">
            {MODES.map((m) => {
              const disabled = isModeDisabled(m.size);
              return (
                <button
                  key={m.size}
                  disabled={disabled}
                  onClick={() => setMode(m.size)}
                  className={cn(
                    'flex-1 rounded-xl border py-3 text-[15px] font-semibold transition-colors',
                    mode === m.size ? 'border-accent-blue bg-accent-blue/15 text-label-primary' : 'border-white/10 bg-bg-secondary text-label-secondary',
                    disabled && mode !== m.size && 'opacity-40',
                  )}
                >
                  {t(m.key)}
                </button>
              );
            })}
          </div>


          <div className="mt-4 text-sm font-medium text-label-secondary">{t('side')}</div>
          <div className="mt-2">
            <TwoSideSelector side={side} onPick={setSide} />
          </div>

          <div className="mt-6">

            {!bots && balance < ticket ? (
              <div className="flex flex-col items-stretch gap-1.5">
                <Button variant="primary" size="l" stretched onClick={() => { onClose(); navigation.navigate('payment'); }}>
                  {t('topUpBalance')}
                </Button>
                <span className="inline-flex items-center justify-center gap-1 text-xs text-label-secondary">
                  {t('notEnoughShort')} <Credits amount={ticket - balance} iconSize={11} />
                </span>
              </div>
            ) : (
              <Button variant="primary" size="l" stretched data-sound="cta" onClick={() => onCreate(mode, ticket, side, bots ? 'bots' : 'ranked')}>
                {bots ? (
                  t('playWithBots')
                ) : (
                  <span className="inline-flex items-center gap-1">
                    {t('createGame')} <Credits amount={ticket} iconSize={15} />
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </BottomSheet>
  );
};

export default CreateGameSheet;
